import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { BlogHero } from "@/components/BlogHero";
import { FeaturedArticle } from "@/components/FeaturedArticle";
import { CategoryGrid } from "@/components/CategoryGrid";
import { Newsletter } from "@/components/Newsletter";
import { BlogPostDisplay } from "@/components/BlogPostDisplay";
import { BlogPost, BlogCategory } from "@/types/blogPost";
import { fetchPublishedBlogPosts, fetchFeaturedBlogPost, fetchBlogCategories } from "@/utils/blogOperations";
import { usePageVisibility, PAGE_VISIBILITY_KEYS } from "@/hooks/usePageVisibility";
import { PageUnavailable } from "@/components/ui/PageUnavailable";

export default function Blog() {
  const { isVisible } = usePageVisibility();
  if (!isVisible(PAGE_VISIBILITY_KEYS.blog)) return <PageUnavailable />;

  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [searchTerm,       setSearchTerm]       = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPost,     setSelectedPost]     = useState<BlogPost | null>(null);
  const [blogPosts,        setBlogPosts]        = useState<BlogPost[]>([]);
  const [featuredPost,     setFeaturedPost]     = useState<BlogPost | null>(null);
  const [categories,       setCategories]       = useState<BlogCategory[]>([]);
  const [loading,          setLoading]          = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [posts, featured, cats] = await Promise.all([
          fetchPublishedBlogPosts(),
          fetchFeaturedBlogPost(),
          fetchBlogCategories(),
        ]);
        setBlogPosts(posts);
        setFeaturedPost(featured);
        setCategories(cats);

        if (slug) {
          const match = posts.find(p => ((p as any).slug ?? String(p.id)) === slug);
          if (match) setSelectedPost(match);
        }
      } catch (e) {
        console.error('Error loading blog data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Reset category filter when searching
  const handleSearchChange = (v: string) => {
    setSearchTerm(v);
    if (v) setSelectedCategory(null);
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch =
      !searchTerm ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (post.excerpt ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (post.subtitle ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      !selectedCategory ||
      (post as any).blog_categories?.id === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // ── Single post view ────────────────────────────────────────────────────────
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 pt-24 pb-8">
          <BlogPostDisplay
            post={selectedPost}
            onBack={() => { setSelectedPost(null); navigate("/blog", { replace: true }); }}
          />
        </div>
        <Footer />
        <FloatingWhatsApp />
        <AccessibilityButton />
      </div>
    );
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  // ── Blog listing ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <BlogHero searchTerm={searchTerm} onSearchChange={handleSearchChange} />

      {!searchTerm && !selectedCategory && (
        <FeaturedArticle featuredPost={featuredPost} onReadPost={setSelectedPost} />
      )}

      <CategoryGrid
        categories={categories.map(cat => ({
          name: cat.name,
          color: 'text-black',
          borderColor: 'border-gray-200',
          id: cat.id,
          color_hex: cat.color_hex,
          border_color_hex: cat.border_color_hex,
          text_color_hex: cat.text_color_hex,
        }))}
        filteredPosts={filteredPosts}
        allPosts={blogPosts}
        onPostSelect={setSelectedPost}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      <Newsletter />
      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
